/**
 * ComboboxField Component
 * Reusable searchable dropdown with live filtering
 * 
 * Created: 2025-11-08
 * Pattern: Based on USASFDivisionSelector searchable dropdown
 * Usage: Selecting from large option lists (instructors, locations, etc.)
 */

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import SearchField from '@/components/form/fields/SearchField';
import { createLogger } from '@/utils/logger/devLogger';

const logger = createLogger('ComboboxField');

export interface ComboboxOption {
  value: string;
  label: string;
  metadata?: Record<string, any>; // Optional additional data
}

export interface ComboboxFieldProps {
  id?: string;
  name?: string;
  label?: string;
  value: string;
  onChange: (value: string, option?: ComboboxOption) => void;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  emptyStateText?: string;
  showResultCount?: boolean;
  clearable?: boolean; // Allow clearing selection
  renderOption?: (option: ComboboxOption, isSelected: boolean) => React.ReactNode; // Custom option rendering
  /**
   * Optional custom search predicate (e.g. fuzzy athlete-name matching).
   * When omitted, the default case-insensitive label/value substring filter
   * applies unchanged.
   */
  filterOption?: (query: string, option: { label: string; value: string }) => boolean;
  /**
   * Called with the search text whenever it changes, for a list the caller
   * loads from the server as the person types.
   */
  onSearchChange?: (query: string) => void;
}

const ComboboxField: React.FC<ComboboxFieldProps> = ({
  id = 'combobox',
  name = 'combobox',
  label,
  value,
  onChange,
  options,
  placeholder = 'Select an option...',
  searchPlaceholder = 'Search...',
  required = false,
  error,
  disabled = false,
  emptyStateText = 'No results found',
  showResultCount = true,
  clearable = true,
  renderOption,
  filterOption,
  onSearchChange,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    onSearchChange?.(searchQuery);
  }, [searchQuery, onSearchChange]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const portalDropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, maxHeight: 384 });

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;

    if (filterOption) {
      return options.filter(option => filterOption(searchQuery, option));
    }

    const query = searchQuery.toLowerCase();
    return options.filter(option =>
      option.label.toLowerCase().includes(query) ||
      option.value.toLowerCase().includes(query)
    );
  }, [options, searchQuery, filterOption]);

  // Get selected option
  const selectedOption = useMemo(() => {
    return options.find(opt => opt.value === value);
  }, [options, value]);

  // Toggle dropdown
  const toggleDropdown = () => {
    if (disabled) return;
    if (!open) {
      calculatePosition(); // Calculate position before opening
    }
    setOpen(!open);
  };

  // Handle option selection
  const handleSelect = (option: ComboboxOption) => {
    onChange(option.value, option);
    setOpen(false);
    setSearchQuery('');
  };

  // Handle clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('', undefined);
    setSearchQuery('');
  };

  // Calculate dropdown position (always using portal like Dropdown component)
  const calculatePosition = () => {
    if (!buttonRef.current) return;
    
    const rect = buttonRef.current.getBoundingClientRect();
    const gap = 4; // Gap between button and dropdown (mt-1 = 4px)
    const estimatedDropdownHeight = Math.min(filteredOptions.length * 42 + 120, 384); // Max height with search input
    
    // Calculate available space above and below
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // Determine if we should flip the dropdown upward
    const shouldFlipUp = spaceBelow < estimatedDropdownHeight + gap && spaceAbove > spaceBelow;
    
    const newPosition = {
      top: shouldFlipUp 
        ? rect.top - estimatedDropdownHeight - gap  // Position above button
        : rect.bottom + gap,                        // Position below button (default)
      left: rect.left,
      width: rect.width,
      maxHeight: shouldFlipUp 
        ? Math.min(spaceAbove - gap - 16, 384)      // Limit height when flipped up
        : Math.min(spaceBelow - gap - 16, 384)      // Limit height when below
    };
    
    setDropdownPosition(newPosition);
  };

  // Calculate position when dropdown opens
  useEffect(() => {
    if (open && buttonRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        calculatePosition();
      });
    }
  }, [open, filteredOptions.length]);

  // Update position on window resize
  useEffect(() => {
    if (open) {
      const handleResize = () => calculatePosition();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [open]);

  // Update position on scroll (like Dropdown component)
  useEffect(() => {
    if (open && buttonRef.current) {
      let rafId: number;
      
      const handleScroll = () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        
        rafId = requestAnimationFrame(() => {
          if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const isVisible = rect.top >= -100 && rect.left >= -100 && 
                            rect.bottom <= window.innerHeight + 100 && 
                            rect.right <= window.innerWidth + 100;
            
            if (!isVisible) {
              setOpen(false); // Close if button scrolled out of view
            } else {
              calculatePosition(); // Recalculate position
            }
          }
        });
      };
      
      // Listen to window scroll
      window.addEventListener('scroll', handleScroll, { passive: true });
      
      // Find and listen to scrollable parent containers
      const scrollableElements: Element[] = [];
      let element = buttonRef.current.parentElement;
      while (element) {
        const style = window.getComputedStyle(element);
        if (style.overflow === 'scroll' || style.overflow === 'auto' || 
            style.overflowY === 'scroll' || style.overflowY === 'auto') {
          scrollableElements.push(element);
        }
        element = element.parentElement;
      }
      
      scrollableElements.forEach(el => {
        el.addEventListener('scroll', handleScroll, { passive: true });
      });
      
      return () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        window.removeEventListener('scroll', handleScroll);
        scrollableElements.forEach(el => {
          el.removeEventListener('scroll', handleScroll);
        });
      };
    }
  }, [open]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is outside both the dropdown container and portal dropdown
      const isOutsideContainer = dropdownRef.current && !dropdownRef.current.contains(target);
      const isOutsidePortal = portalDropdownRef.current && !portalDropdownRef.current.contains(target);
      const isOutsideButton = buttonRef.current && !buttonRef.current.contains(target);
      
      if (isOutsideContainer && isOutsidePortal && isOutsideButton) {
        setOpen(false);
        setSearchQuery('');
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [open]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div
        ref={dropdownRef}
        className="relative"
        onKeyDown={handleKeyDown}
      >
        {/* Trigger Button */}
        <button
          ref={buttonRef}
          type="button"
          id={id}
          onClick={toggleDropdown}
          disabled={disabled}
          className={`
            h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-sm 
            focus:outline-hidden focus:ring-3 dark:bg-gray-900 dark:text-white/90
            text-left relative
            ${clearable && selectedOption && !disabled ? 'pr-20' : 'pr-10'}
            ${disabled 
              ? 'text-gray-500 border-gray-300 opacity-40 bg-gray-100 cursor-not-allowed dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
              : error
              ? 'border-error-300 focus:border-error-300 focus:ring-error-500/20 dark:border-error-700'
              : 'bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/20 dark:border-gray-700 dark:text-white/90 dark:focus:border-brand-800'
            }
          `}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={`truncate ${selectedOption ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
            {selectedOption?.label || placeholder}
          </span>
          
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <GoogleMaterialIcon 
              name={open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} 
              size="sm"
              className="text-gray-500"
            />
          </span>
        </button>
        
        {/* Clear Button - Positioned absolutely outside main button */}
        {clearable && selectedOption && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear(e);
            }}
            className="absolute right-10 top-1/2 -translate-y-1/2 z-10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
            aria-label="Clear selection"
          >
            <GoogleMaterialIcon name="close" size="sm" />
          </button>
        )}

        {/* Dropdown Panel - Always use portal rendering (like Dropdown component) */}
        {open && typeof window !== 'undefined' && createPortal(
          <div 
            ref={portalDropdownRef}
            className="fixed bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              maxHeight: `${dropdownPosition.maxHeight}px`,
              zIndex: 999999,
              position: 'fixed'
            }}
          >
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10">
              <SearchField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={() => setSearchQuery('')}
                placeholder={searchPlaceholder}
                autoFocus
              />

              {/* Result Counter */}
              {showResultCount && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Showing {filteredOptions.length} of {options.length}
                </div>
              )}
            </div>

            {/* Options List */}
            <div className="overflow-y-auto" style={{ maxHeight: `${(dropdownPosition.maxHeight || 384) - 120}px` }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isSelected = option.value === value;
                  
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSelect(option)}
                      className={`
                        w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800
                        flex items-center justify-between transition-colors
                        ${isSelected ? 'bg-brand-50 dark:bg-brand-900/20 border-l-4 border-brand-500' : ''}
                      `}
                    >
                      {renderOption ? (
                        renderOption(option, isSelected)
                      ) : (
                        <>
                          <span className="text-gray-900 dark:text-white">
                            {option.label}
                          </span>
                          {isSelected && (
                            <GoogleMaterialIcon 
                              name="check" 
                              size="sm" 
                              className="text-brand-600 dark:text-brand-400"
                            />
                          )}
                        </>
                      )}
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center">
                  <GoogleMaterialIcon 
                    name="search_off" 
                    size="lg" 
                    className="text-gray-300 dark:text-gray-600 mb-2"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {emptyStateText}
                  </p>
                  {searchQuery && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Try adjusting your search
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>,
          document.body
        )}

        {/* Hidden select for form submission */}
        <select
          name={name}
          value={value}
          onChange={() => {}} // Controlled by button
          required={required}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        >
          <option value="">Select...</option>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-2 text-xs text-error-500">{error}</p>
      )}
    </div>
  );
};

export default ComboboxField;

